/**
 * Product types for auth differentiation
 * Per auth-routing-eng-plan.md
 */
export type Product = "VISIT_ASSIST" | "ENGAGE" | "INFLECT";

/**
 * Session timeout configuration per product
 * ENGAGE: 30 minutes (HIPAA)
 * VISIT_ASSIST: 24 hours (user-friendly)
 * INFLECT: 30 minutes (enterprise)
 */
export const SESSION_TIMEOUTS: Record<Product, number> = {
  ENGAGE: 30 * 60 * 1000, // 30 minutes
  VISIT_ASSIST: 24 * 60 * 60 * 1000, // 24 hours
  INFLECT: 30 * 60 * 1000, // 30 minutes (enterprise)
};

/**
 * Warning threshold before timeout (in ms)
 * Show warning modal at this time before logout
 */
export const INACTIVITY_WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes before timeout

/**
 * Token refresh interval for active users
 */
export const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000; // 20 minutes

/**
 * Subset of the `GET /user/me/full` profile kept in the auth store.
 */
export interface AuthUser {
  user_id: string;
  user_email: string;
  personal_info?: {
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

export interface AuthState<TUser extends AuthUser = AuthUser> {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  product: Product | null;
  /** Profile from the last successful checkAuth; null when signed out or not yet loaded */
  user: TUser | null;
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setError: (error: string | null) => void;
  setProduct: (product: Product) => void;
  reset: () => void;
}

export type AuthStore<TUser extends AuthUser = AuthUser> = AuthState<TUser> &
  AuthActions;

// ── MFA constants ─────────────────────────────────────────────────────────────

/** Backend 403 detail string indicating TOTP verification is pending */
export const MFA_DETAIL_VERIFY = "MFA verification required" as const;
/** Backend 403 detail string indicating the user hasn't enrolled in MFA yet */
export const MFA_DETAIL_ENROLL = "MFA enrollment required" as const;

// ── MFA extension types ───────────────────────────────────────────────────────

export interface MfaLoginResult {
  requiresMfa: boolean;
  requiresEnrollment: boolean;
}

export interface MfaState {
  /** True when the user is authenticated but hasn't completed TOTP this session */
  mfaVerificationRequired: boolean;
  /** True when the user is authenticated but hasn't enrolled in MFA yet */
  mfaEnrollmentRequired: boolean;
}

export interface MfaActions {
  login: (email: string, password: string) => Promise<MfaLoginResult>;
  setMfaVerificationRequired: (value: boolean) => void;
  setMfaEnrollmentRequired: (value: boolean) => void;
  clearMfaFlags: () => void;
}

/**
 * Full MFA-capable auth store type.
 * Replaces the base `login` signature with one that returns MFA flags.
 */
export type MfaAuthStore = Omit<AuthStore, "login"> & MfaState & MfaActions;

export interface SessionGuardConfig {
  /** Product determines timeout duration (pulled from SESSION_TIMEOUTS — not overridable) */
  product: Product;
  /** Callback when session expires or is terminated */
  onLogout: () => void;
  /** Callback when inactivity warning should be shown */
  onWarning?: () => void;
  /** Callback when session activity is detected or the session is extended */
  onActivity?: () => void;
  /**
   * How many ms before the timeout to trigger the warning.
   * Defaults to INACTIVITY_WARNING_THRESHOLD (5 minutes).
   * Pass 15 * 60 * 1000 for VISIT_ASSIST's 15-minute warning.
   */
  warningMs?: number;
  /**
   * DOM events treated as user activity.
   * Must be a stable reference (module-level const) to avoid re-registering listeners.
   * Defaults to DEFAULT_ACTIVITY_EVENTS.
   */
  activities?: readonly string[];
}
